import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Custom SVG Icons
const GitHubIcon = () => (
  <motion.div 
    whileHover={{ scale: 1.3, rotate: 10 }}
    whileTap={{ scale: 0.9 }}
    className="text-white hover:text-purple-400 transition-colors"
  >
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="currentColor"
    >
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
    </svg>
  </motion.div>
);

const LinkedInIcon = () => (
  <motion.div 
    whileHover={{ scale: 1.3, rotate: -10 }}
    whileTap={{ scale: 0.9 }}
    className="text-white hover:text-blue-400 transition-colors"
  >
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="currentColor"
    >
      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
    </svg>
  </motion.div>
);

const TwitterIcon = () => (
  <motion.div 
    whileHover={{ scale: 1.3, rotate: 5 }}
    whileTap={{ scale: 0.9 }}
    className="text-white hover:text-cyan-400 transition-colors"
  >
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="currentColor"
    >
      <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
    </svg>
  </motion.div>
);

const FreelancerPortfolio = () => {
  const [activeSection, setActiveSection] = useState('about');
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [hoverText, setHoverText] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const cursorRef = useRef(null);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Floating particles effect - fewer particles on mobile
  const particles = Array.from({ length: isMobile ? 15 : 30 }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 5 + 1,
    speed: Math.random() * 2 + 0.5,
    delay: Math.random() * 5
  }));

  // Mouse move effect for custom cursor - disable on mobile
  useEffect(() => {
    if (isMobile) return;
    
    const handleMouseMove = (e) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isMobile]);

  const projects = [
    {
      title: 'Iron Clad Fitness',
      description: 'Wesbsite for a gym and fitness center with class scheduling and trainer profiles',
      technologies: ['NodeMailer', 'NextJs', 'React','TailwindCSS', 'UI/UX Design'],
      gradient: 'from-black-500 via-cyan-500 to-brown-500',
      image: 'media/gym.jpg',
      link: 'https://iron-clad-fitness-haven.vercel.app/'
    },
    {
      title: 'UpVibe',
      description: 'A Social Media management platform that leverages AI to optimize content scheduling and audience engagement across multiple channels.',
      technologies: ['NodeMailer', 'NextJs', 'React','TailwindCSS', 'UI/UX Design'],
      gradient: 'from-purple-500 via-yellow-500 to-brown-500',
      image: 'media/upvibe.png',
      link: 'https://up-vibe-frontend.vercel.app/'
    },
    {
      title: 'PowerFit Fitness',
      description: 'Wesbsite for a gym and fitness center with class scheduling and trainer profiles',
      technologies: ['Lucide React', 'NextJs', 'React','TailwindCSS', 'UI/UX Design'],
      gradient: 'from-gray-800 via-gray-900 to-black',
      image: 'media/powerfit.png',
      link: 'https://v0-modern-gym-website-eight.vercel.app/'
    },
    {
      title: 'Attendee',
      description: 'Attendance Management system for students and teachers',
      technologies: ['Nodejs', 'NextJs','MongoDB', 'Express.js', 'React','TailwindCSS'],
      gradient: 'from-purple-500 via-yellow-500 to-brown-500',
      image: 'media/attendee.jpg',
      link: 'https://attendee-dun.vercel.app/'
    },
    {
      title: 'Connectly',
      description: 'AI-powered professional networking platform',
      technologies: ['Nodejs', 'Figma', 'NextJs','MongoDB', 'Express.js', 'Docker'],
      gradient: 'from-red-500 via-orange-500 to-yellow-500',
      image: 'media/connectly.jpg',
      link: 'https://connectly-steel.vercel.app/'
    },
    {
      title: 'Wanderlust',
      description: 'Cutting-edge tourist platform with review feature',
      technologies: ['HTML','CSS','Javascript', 'MongoDB', 'Express.js', 'Node.js'],
      gradient: 'from-purple-500 via-pink-500 to-red-500',
      image: 'media/wanderlust.png',
      link: '#'
    }
    
  ];

  const skills = [
    { name: 'React', level: 95 },
    { name: 'Javascript', level: 90 },
    { name: 'Node.js', level: 85 },
    { name: 'Express.js', level: 80 },
    { name: 'MongoDB', level: 90 },
    { name: 'UI/UX Strategy', level: 88 },
    { name: 'Docker', level: 75 }
  ];

  const stats = [
    { value: '10+', label: 'Projects Completed' },
    { value: '100%', label: 'Client Satisfaction' },
    { value: '10+', label: 'Technologies Mastered' },
    { value: '∞', label: 'Creative Ideas' }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black text-white overflow-hidden">
      {/* Floating particles background */}
      <div className="fixed inset-0 overflow-hidden z-0">
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute rounded-full bg-purple-500 opacity-20"
            style={{
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              left: `${particle.x}%`,
              top: `${particle.y}%`,
            }}
            animate={{
              y: [0, 50, 0],
              x: [0, 20, 0],
            }}
            transition={{
              duration: particle.speed * 10,
              repeat: Infinity,
              repeatType: 'reverse',
              delay: particle.delay
            }}
          />
        ))}
      </div>

      {/* Custom animated cursor - only show on desktop */}
      {!isMobile && (
        <>
          <motion.div
            ref={cursorRef}
            className="fixed w-8 h-8 rounded-full bg-purple-500 bg-opacity-30 pointer-events-none z-50 mix-blend-difference"
            animate={{
              x: cursorPosition.x - 16,
              y: cursorPosition.y - 16,
              scale: isHovering ? 2.5 : 1,
            }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          />
          {isHovering && (
            <motion.div
              className="fixed text-white text-xs font-bold px-2 py-1 rounded-full bg-purple-600 pointer-events-none z-50"
              animate={{
                x: cursorPosition.x + 20,
                y: cursorPosition.y + 20,
              }}
            >
              {hoverText}
            </motion.div>
          )}
        </>
      )}

      {/* Animated Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 100 }}
        className="sticky top-0 z-50 bg-opacity-80 backdrop-blur-md bg-gray-800 shadow-2xl"
      >
        <div className="container mx-auto px-4 py-3 md:p-4 flex flex-col md:flex-row justify-between items-center">
          <motion.h1 
            className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600 mb-2 md:mb-0"
            animate={{
              textShadow: ['0 0 8px rgba(192, 132, 252, 0)', '0 0 8px rgba(192, 132, 252, 0.8)', '0 0 8px rgba(192, 132, 252, 0)']
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatType: 'reverse'
            }}
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setActiveSection('about')}
            onMouseEnter={() => !isMobile && (setIsHovering(true), setHoverText('HOME'))}
            onMouseLeave={() => !isMobile && setIsHovering(false)}
          >
            Priyam Ghosh
          </motion.h1>
          <div className="flex flex-wrap justify-center gap-2 md:gap-4">
            {['about', 'projects', 'skills', 'stats'].map(section => (
              <motion.button 
                key={section}
                onClick={() => setActiveSection(section)}
                onMouseEnter={() => !isMobile && (setIsHovering(true), setHoverText(section.toUpperCase()))}
                onMouseLeave={() => !isMobile && setIsHovering(false)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className={`capitalize px-3 py-1 md:px-4 md:py-2 rounded-full transition-all duration-300 text-sm md:text-base ${
                  activeSection === section 
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg' 
                    : 'text-gray-300 hover:text-white hover:bg-gray-700'
                }`}
              >
                {section}
              </motion.button>
            ))}
          </div>
        </div>
      </motion.nav>

      {/* Main Content */}
      <main className="flex-grow container mx-auto px-4 sm:px-6 py-8 md:py-12 relative z-10">
        <AnimatePresence mode="wait">
          {activeSection === 'about' && (
            <motion.section
              key="about"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col lg:flex-row items-center justify-center gap-8 md:gap-12"
            >
              <div className="w-full md:w-1/2 lg:w-1/3 relative">
                <motion.img 
                  src="media/portfolio_pic.jpg" 
                  alt="Profile" 
                  className="w-48 h-48 md:w-64 md:h-64 lg:w-72 lg:h-72 rounded-full border-4 border-purple-500 shadow-2xl mx-auto"
                  whileHover={{ rotate: 5, scale: 1.05 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                />
                <motion.div 
                  className="absolute -inset-4 border-2 border-purple-500 rounded-full opacity-0"
                  animate={{
                    opacity: [0, 0.5, 0],
                    scale: [1, 1.2, 1]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity
                  }}
                />
              </div>
              <div className="w-full md:w-1/2 lg:w-1/2 space-y-4 md:space-y-6 text-center md:text-left">
                <motion.h2 
                  className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-2 md:mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600"
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  Digital Alchemist
                </motion.h2>
                <motion.p 
                  className="text-base sm:text-lg md:text-xl text-gray-300 leading-relaxed"
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  I transform complex technological challenges into elegant, user-centric solutions. 
                  With a decade of experience at the intersection of design and engineering, 
                  I create digital experiences that are as beautiful as they are functional.
                </motion.p>
                <motion.div 
                  className="flex justify-center md:justify-start gap-4 md:gap-6"
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <a 
                    href="https://github.com/priyam191" 
                    className="transform transition-all"
                    onMouseEnter={() => !isMobile && (setIsHovering(true), setHoverText('GITHUB'))}
                    onMouseLeave={() => !isMobile && setIsHovering(false)}
                  >
                    <GitHubIcon />
                  </a>
                  <a 
                    href="https://www.linkedin.com/in/priyam-ghosh-8a486727b/" 
                    className="transform transition-all"
                    onMouseEnter={() => !isMobile && (setIsHovering(true), setHoverText('LINKEDIN'))}
                    onMouseLeave={() => !isMobile && setIsHovering(false)}
                  >
                    <LinkedInIcon />
                  </a>
                  <a 
                    href="https://x.com/PGhosh191" 
                    className="transform transition-all"
                    onMouseEnter={() => !isMobile && (setIsHovering(true), setHoverText('TWITTER'))}
                    onMouseLeave={() => !isMobile && setIsHovering(false)}
                  >
                    <TwitterIcon />
                  </a>
                </motion.div>
              </div>
            </motion.section>
          )}

          {activeSection === 'projects' && (
            <motion.section
              key="projects"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <motion.h2 
                className="text-3xl md:text-4xl font-bold text-center mb-8 md:mb-12 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600"
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                Boundary-Pushing Projects
              </motion.h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {projects.map((project, index) => (
                  <motion.div 
                    key={index}
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                    whileHover={{ y: isMobile ? 0 : -10 }}
                    onMouseEnter={() => !isMobile && (setIsHovering(true), setHoverText('VIEW PROJECT'))}
                    onMouseLeave={() => !isMobile && setIsHovering(false)}
                    className={`bg-gradient-to-br ${project.gradient} rounded-2xl p-4 md:p-6 shadow-2xl overflow-hidden relative group h-full`}
                  >
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-500 z-10" />
                    <div className="relative z-20 h-full flex flex-col">
                      <div className="h-40 sm:h-48 overflow-hidden rounded-xl mb-4 md:mb-6">
                        <img 
                          src={project.image} 
                          alt={project.title} 
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-3">{project.title}</h3>
                      <p className="text-white text-opacity-80 mb-3 md:mb-4 flex-grow text-sm md:text-base">{project.description}</p>
                      <div className="flex flex-wrap gap-1 md:gap-2 mb-3 md:mb-4">
                        {project.technologies.map(tech => (
                          <motion.span 
                            key={tech} 
                            whileHover={{ scale: isMobile ? 1 : 1.1 }}
                            className="bg-white bg-opacity-20 text-white text-xs px-2 py-1 rounded-full"
                          >
                            {tech}
                          </motion.span>
                        ))}
                      </div>
                      <motion.a
                        href={project.link}
                        className="inline-block mt-auto px-3 py-1 md:px-4 md:py-2 bg-white bg-opacity-20 rounded-full text-xs md:text-sm font-semibold hover:bg-opacity-30 transition-all"
                        whileHover={{ scale: isMobile ? 1 : 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Explore Case Study
                      </motion.a>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          )}

          {activeSection === 'skills' && (
            <motion.section
              key="skills"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-gray-800 bg-opacity-50 rounded-2xl p-6 md:p-12 backdrop-blur-sm"
            >
              <motion.h2 
                className="text-3xl md:text-4xl font-bold text-center mb-8 md:mb-12 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600"
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                Technological Mastery
              </motion.h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {skills.map((skill, index) => (
                  <motion.div 
                    key={index}
                    initial={{ x: index % 2 === 0 ? -50 : 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                    className="space-y-2"
                  >
                    <div className="flex justify-between">
                      <span className="font-medium text-sm md:text-base">{skill.name}</span>
                      <span className="text-purple-400 text-sm md:text-base">{skill.level}%</span>
                    </div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                        initial={{ width: 0 }}
                        animate={{ width: `${skill.level}%` }}
                        transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          )}

          {activeSection === 'stats' && (
            <motion.section
              key="stats"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <motion.h2 
                className="text-3xl md:text-4xl font-bold text-center mb-8 md:mb-12 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600"
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                By The Numbers
              </motion.h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
                {stats.map((stat, index) => (
                  <motion.div 
                    key={index}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ 
                      type: 'spring',
                      stiffness: 100,
                      damping: 10,
                      delay: index * 0.15
                    }}
                    whileHover={{ scale: isMobile ? 1 : 1.05 }}
                    className="bg-gradient-to-br from-gray-800 to-gray-900 p-4 md:p-6 rounded-xl shadow-xl border border-gray-700"
                  >
                    <motion.p 
                      className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600 mb-1 md:mb-2"
                      animate={{
                        scale: [1, 1.1, 1],
                        textShadow: ['0 0 0px rgba(192, 132, 252, 0)', '0 0 10px rgba(192, 132, 252, 0.8)', '0 0 0px rgba(192, 132, 252, 0)']
                      }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        repeatType: 'reverse',
                        delay: index * 0.5
                      }}
                    >
                      {stat.value}
                    </motion.p>
                    <p className="text-gray-400 text-sm md:text-base">{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </main>

      {/* Sticky Footer */}
      <motion.footer 
        className="bg-gray-900 bg-opacity-80 backdrop-blur-md text-center py-4 md:py-6 w-full"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <div className="container mx-auto px-4">
          <p className="text-gray-400 mb-3 md:mb-4 text-sm md:text-base">
            Ready to create something extraordinary?
          </p>
          <motion.a
            href="mailto:ghoshpriyam191@gmail.com"
            className="inline-block px-6 py-2 md:px-8 md:py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full font-bold shadow-lg text-sm md:text-base"
            whileHover={{ 
              scale: isMobile ? 1 : 1.05,
              boxShadow: '0 0 15px rgba(192, 132, 252, 0.6)'
            }}
            whileTap={{ scale: 0.95 }}
            onMouseEnter={() => !isMobile && (setIsHovering(true), setHoverText("LET'S TALK"))}
            onMouseLeave={() => !isMobile && setIsHovering(false)}
          >
            Contact Me
          </motion.a>
          <p className="text-gray-500 mt-4 md:mt-6 text-xs md:text-sm">
            © {new Date().getFullYear()} Priyam Ghosh. Crafting the future, one pixel at a time.
          </p>
        </div>
      </motion.footer>
    </div>
  );
};

export default FreelancerPortfolio;
